/**
 * The Sync-now control: a verb+object button plus an INLINE result panel that renders the sync-now
 * state machine explicitly. Design rule honoured here — a block is LOUD (the human reason + the quota
 * snapshot + when-to-retry), `deduped` is visibly distinct, and the live FreshnessBadge (ดึงสดเมื่อ)
 * appears ONLY on `ok` and is visually distinct from the mirror badge.
 *
 * It also raises a toast on every settled outcome so an operator who has scrolled away still notices
 * (errors/blocks are assertive; ok/deduped are polite). The inline panel is the durable record; the
 * toast is the nudge.
 */

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, CopyCheck, RefreshCw } from "lucide-react";
import { Button, useNotifier } from "@/components/ui";
import { FreshnessBadge } from "@/components/portal/FreshnessBadge";
import { formatInstant, formatInt } from "@/lib/format";
import { cn } from "@/lib/cn";
import type { QuotaSnapshot, SyncNowStatus } from "@/api/types";
import { asTKey, type TKey, type TKeyStartingWith } from "@/i18n/keys";
import { useSyncNow, type SyncNowMachine } from "./useSyncNow";
import type { SyncNowEnvelope } from "@/api/types";

interface SyncNowControlProps {
  label: string;
  fetcher: () => Promise<SyncNowEnvelope>;
  onOk?: () => void;
  className?: string;
  /** When the account's plan/usage mirror was last refreshed — shown as a muted caption by the button. */
  lastSyncedAt?: string | null;
}

/**
 * Maps a structured block status → the i18n key for its human "what & when-to-retry" copy.
 *
 * BOTH sides are narrow, and both halves of that are load-bearing:
 *
 *  - VALUES are `TKeyStartingWith<"syncNow.blocked.">`, not `string`. A `Record<_, string>` would widen
 *    them and the keys would go unchecked — the whole point of this PR.
 *  - KEYS are the real `blocked_*` union, not `string`. This is the half that was still widened. Keyed
 *    by `string`, tsc has nothing to check exhaustiveness against: the day the backend adds a fourth
 *    block state to `SyncNowStatus`, this map does NOT fail to compile, the lookup misses, the `??`
 *    below swallows it, and every operator blocked for that reason is shown the vague generic copy
 *    instead of the real one. Silent, and it degrades the exact diagnostic this copy exists to give.
 *    Keyed by the union, that same day is a BUILD failure that names the missing status.
 *
 * `as const satisfies …` — NOT a `: Record<…>` annotation, which would re-widen the values.
 */
const BLOCK_COPY_KEY = {
  blocked_quota: "syncNow.blocked.quota",
  blocked_throttle: "syncNow.blocked.throttle",
  blocked_daily_sync: "syncNow.blocked.dailySync",
} as const satisfies Record<
  Extract<SyncNowStatus, `blocked_${string}`>,
  TKeyStartingWith<"syncNow.blocked.">
>;

export function SyncNowControl({
  label,
  fetcher,
  onOk,
  className,
  lastSyncedAt,
}: SyncNowControlProps) {
  const { t } = useTranslation();
  const { notify } = useNotifier();
  const { state, isPending, run } = useSyncNow(fetcher, onOk);

  // One toast per settled transition (assertive for blocks/errors). Ref-guarded so re-renders don't spam.
  const lastNotified = useRef<string>("idle");
  useEffect(() => {
    if (state.phase === lastNotified.current || state.phase === "loading") return;
    lastNotified.current = state.phase;
    if (state.phase === "ok") {
      notify({ tone: "ok", title: t("syncNow.success") });
    } else if (state.phase === "deduped") {
      notify({ tone: "info", title: t("syncNow.deduped") });
    } else if (state.phase === "blocked") {
      notify({
        tone: "warn",
        title: t("syncNow.title"),
        body: t(BLOCK_COPY_KEY[state.status] ?? "syncNow.blocked.generic"),
        durationMs: 8000, // auto-dismiss; the inline ResultPanel keeps the durable record
      });
    } else if (state.phase === "error") {
      notify({
        tone: "danger",
        title: t("syncNow.title"),
        body: errorBody(t, state),
        durationMs: 8000, // auto-dismiss; the inline ResultPanel keeps the durable record
      });
    }
  }, [state, notify, t]);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Button
        variant="secondary"
        size="sm"
        loading={isPending}
        onClick={run}
        className="self-start"
      >
        {!isPending && <RefreshCw className="size-4" aria-hidden />}
        {label}
      </Button>
      {lastSyncedAt && (
        <span className="text-(length:--text-2xs) text-(--text-muted)">
          {t("syncNow.lastSynced", { time: formatInstant(lastSyncedAt) })}
        </span>
      )}
      <ResultPanel state={state} />
    </div>
  );
}

function ResultPanel({ state }: { state: SyncNowMachine }) {
  const { t } = useTranslation();
  if (state.phase === "idle" || state.phase === "loading") return null;

  if (state.phase === "ok") {
    return (
      <div
        role="status"
        className="flex flex-col gap-2 rounded-(--radius) border border-(--ok-subtle) bg-(--ok-subtle) p-3.5"
      >
        <span className="inline-flex items-center gap-1.5 text-(length:--text-sm) font-semibold text-(--ok)">
          <CheckCircle2 className="size-4" aria-hidden />
          {t("syncNow.success")}
        </span>
        {/* The LIVE badge — distinct from the mirror badge; only ever shown here. It IS the "synced
            successfully at <time>" confirmation; the internal quota/request budget is deliberately not
            shown to staff on success (only on a quota block, where it explains the why). */}
        <FreshnessBadge kind="live" timestamp={state.liveAt} />
      </div>
    );
  }

  if (state.phase === "deduped") {
    return (
      <div
        role="status"
        className="flex flex-col gap-2 rounded-(--radius) border border-(--info-subtle) bg-(--info-subtle) p-3.5"
      >
        <span className="inline-flex items-center gap-1.5 text-(length:--text-sm) font-semibold text-(--info)">
          <CopyCheck className="size-4" aria-hidden />
          {t("syncNow.deduped")}
        </span>
      </div>
    );
  }

  if (state.phase === "blocked") {
    // Unmapped reason codes fall back to a localized generic, never the raw English code. `asTKey` is
    // what enforces that now: a reason with no copy returns null, so we render the generic. (The old
    // `t(key, { defaultValue })` form typed the key as `string` and switched the gate off for the call.)
    const reason = asTKey("syncNow.reason.", state.reason ?? "");
    return (
      <div
        role="alert"
        className="flex flex-col gap-2 rounded-(--radius) border border-(--warn-subtle) bg-(--warn-subtle) p-3.5"
      >
        <span className="inline-flex items-center gap-1.5 text-(length:--text-sm) font-semibold text-(--warn)">
          <AlertTriangle className="size-4" aria-hidden />
          {t(BLOCK_COPY_KEY[state.status] ?? "syncNow.blocked.generic")}
        </span>
        {state.reason && (
          <p className="text-(length:--text-xs) text-(--text-muted)">
            {reason ? t(reason) : t("syncNow.blocked.generic")}
          </p>
        )}
        <QuotaReadout quota={state.quota} />
      </div>
    );
  }

  // error
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-(--radius) border border-(--danger-subtle) bg-(--danger-subtle) p-3.5"
    >
      {/* Title uses --danger-strong-fg (AA on --danger-subtle); the icon keeps the --danger hue. */}
      <span className="inline-flex items-center gap-1.5 text-(length:--text-sm) font-semibold text-(--danger-strong-fg)">
        <AlertTriangle className="size-4 text-(--danger)" aria-hidden />
        {errorBody(t, state)}
      </span>
      {state.httpError?.requestId && (
        <p className="text-(length:--text-2xs) text-(--text-subtle)">
          {t("state.requestId", { id: state.httpError.requestId })}
        </p>
      )}
      <QuotaReadout quota={state.quota} />
    </div>
  );
}

// Deliberately NOT `ReturnType<typeof useTranslation>["t"]` — that exact overloaded generic type is
// expensive for tsc to resolve against a large resource tree, and errorBody()'s call sites here are all
// plain (a bare key, no interpolation options), so a narrow structural alias is both sufficient and far
// cheaper to check. (Measured: with the Pool module's ~150 additional i18n keys, the full generic type
// pushed this specific call site over tsc's "type instantiation is excessively deep" limit — see the
// commit that added this comment for the failing error. Do not widen this back without re-measuring.)
type TFn = (key: TKey) => string;

/**
 * Error copy. A non-envelope failure (caught in useSyncNow.onError) carries an httpError; its STATUS,
 * not the synthetic reason code, decides the message — an expired session (401) or lost permission
 * (403) or a transport failure (0) must NOT read as a "could not fetch from Zoom" outage, which would
 * steer the operator to retry instead of re-authenticating / checking the network.
 *
 * Envelope errors (status "error" reason e.g. zoom_error:NNN, zoom-app-not-configured) keep the
 * mapped Zoom copy.
 */
function errorBody(t: TFn, state: Extract<SyncNowMachine, { phase: "error" }>): string {
  const status = state.httpError?.status;
  if (status === 401 || status === 403) return t("syncNow.error.authExpired");
  if (status === 0) return t("state.networkError");
  // A reason code we have no copy for is not a reason we can show — fall through to the generic error.
  const reason = state.reason ? asTKey("syncNow.reason.", state.reason) : null;
  if (reason) return t(reason);
  return t("syncNow.blocked.error");
}

/** Compact quota readout: on-demand remaining / total remaining. Hidden when the snapshot is absent. */
function QuotaReadout({ quota }: { quota: QuotaSnapshot | null }) {
  const { t } = useTranslation();
  if (!quota) return null;
  return (
    <dl className="flex flex-wrap items-center gap-x-4 gap-y-1 text-(length:--text-2xs) text-(--text-muted)">
      <div className="inline-flex items-center gap-1.5">
        <dt>{t("syncNow.quota.onDemandRemaining")}</dt>
        <dd className="tabular font-medium text-(--text)">
          {formatInt(quota.on_demand_remaining)}
        </dd>
      </div>
      <div className="inline-flex items-center gap-1.5">
        <dt>{t("syncNow.quota.capRemaining")}</dt>
        <dd className="tabular font-medium text-(--text)">{formatInt(quota.cap_remaining)}</dd>
      </div>
      <div className="inline-flex items-center gap-1.5">
        <dt className="sr-only">{t("syncNow.quota.usedLabel")}</dt>
        <dd className="tabular text-(--text-subtle)">
          {t("syncNow.quota.used", {
            used: formatInt(quota.total_used),
            total: formatInt(quota.cap),
          })}
        </dd>
      </div>
    </dl>
  );
}
